import React from 'react'
import { Button, EyeIcon, Flex, H2, Input, Line, Span } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { OpportunityInterestListItem } from '../../lib/types'
import { displayDollar, getTimestampWithTimezone } from '../../lib/utils/helpers'
import './allocation-requests.scss'

export type AllocationRequestsListPresenterProps = {
  interests: OpportunityInterestListItem[]
  isLoading: boolean
  keyword: string
  handleKeywordChange: (value: string) => void
  resetSearch: () => void
  pagination: { total: number; limit: number; count: number; offset: number }
  currentPage: number
  handleCurrentPage: (page: number) => void
  handleView: (row: OpportunityInterestListItem) => void
}

const clientName = (row: OpportunityInterestListItem) => {
  const name = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim()
  return name || '—'
}

const formatDate = (value?: string) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return getTimestampWithTimezone(date, 'DD MMM YYYY, HH:mm z')
}

export function AllocationRequestsListPresenter({
  interests,
  isLoading,
  keyword,
  handleKeywordChange,
  resetSearch,
  pagination,
  currentPage,
  handleCurrentPage,
  handleView,
}: AllocationRequestsListPresenterProps) {
  return (
    <Flex variant='column-center'>
      <div className='allocation-requests w-100'>
        <H2 classes={['mb-48']} text='Allocation Requests' />
        <div className='toolbar-layout gap-12 mb-16 mt-16'>
          <Input
            name='keyword'
            value={keyword}
            handleChange={(e) => handleKeywordChange(e.target.value)}
            placeholder='Search by client, opportunity, or ID'
            dataTestId='allocation-requests-search'
          />
          <div className='sort-reset'>
            <Button
              handleClick={resetSearch}
              classes={['reset-btn']}
              variant='secondary-btn'
              text='Reset'
              dataTestId='allocation-requests-reset'
            />
          </div>
        </div>
        <Line />
        {isLoading ? (
          <p className='allocation-requests__loading' data-testid='allocation-requests-loading'>
            Loading…
          </p>
        ) : interests.length === 0 ? (
          <Span
            classes={['center', 'mt-24']}
            variant='thin'
            text='No Allocation Requests Found'
            dataTestId='allocation-requests-empty'
          />
        ) : (
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='allocation-requests-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Client Email</th>
                    <th>Client ID</th>
                    <th>Opportunity</th>
                    <th>Amount</th>
                    <th>Submitted</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {interests.map((row) => (
                    <tr key={row.id} data-testid={`allocation-request-row-${row.id}`}>
                      <td>{clientName(row)}</td>
                      <td>{row.investor_email || '—'}</td>
                      <td>{row.investor_reference || '—'}</td>
                      <td>{row.opportunity_title || '—'}</td>
                      <td data-testid={`allocation-request-amount-${row.id}`}>
                        {displayDollar(String(row.amount), row.currency_code || 'USD', 2, true)}
                      </td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        <EyeIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() => handleView(row)}
                          dataTestId={`allocation-request-view-${row.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Flex>
            {pagination.total > pagination.limit && (
              <Pagination
                pagination={pagination}
                handleCurrentPage={handleCurrentPage}
                currentPage={currentPage}
              />
            )}
          </div>
        )}
      </div>
    </Flex>
  )
}
